import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Row, Col, Typography, Card, Flex, Button, Checkbox, Avatar, Table, Input, Tag, DatePicker, Form, Space, Drawer, Popconfirm, Spin, message } from 'antd';
import { PiCoinsDuotone, PiGiftDuotone, PiListPlusDuotone, PiUserDuotone, PiUserFocusDuotone, PiUserPlusDuotone, PiUsersDuotone, PiUserSoundDuotone } from 'react-icons/pi';
import { SearchOutlined, ExportOutlined, EyeOutlined, DeleteOutlined, UserAddOutlined, EditOutlined, LoadingOutlined } from '@ant-design/icons';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { FiMoreVertical } from 'react-icons/fi';
import dayjs from 'dayjs';
Chart.register(...registerables);
const { Title } = Typography;

const Home = () => {
    const [dataLoading, setDataLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [planLoading, setPlanLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [modifyPlanDrawer, setModifyPlanDrawer] = useState(false);
    const [planform] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const currentMonth = dayjs().format('YYYY-MM');
    const previousMonth = dayjs().subtract(1, 'month').format('YYYY-MM');
    const totalSales = 7841.12;
    const donughtData = {
        labels: ['Total Customers', 'Campaigns'],
        datasets: [
            {
                data: [5495, 2000],
                backgroundColor: ['#04BEBD', '#EBBD07'],
                borderWidth: 0,
            },
        ],
    };


    const fetchCustomerOverviewData = useCallback(async () => {
        const url = `${window.API_BASE_URL}/superadmin/users`;
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            console.log(data);
            if (response.ok) {
                setUsers(data?.users);
                setFilteredData(data?.users);
            }
        } catch (error) {
            console.error('Error during fetching store data:', error);
        } finally {
            setDataLoading(false);
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            await fetchCustomerOverviewData();
            setDataLoading(false);
        };
        fetchData();
    }, [fetchCustomerOverviewData]);

    const donughtOptions = {
        cutout: '75%',
        plugins: {
            tooltip: {
                enabled: true,
            },
            legend: {
                display: false,
            },
        },
    };

    const generateRandomData = () => {
        const startDate = new Date(dayjs().subtract(29, 'day').format('YYYY-MM-DD'));
        let remainingSales = totalSales;
        const data = [];

        for (let i = 0; i < 30; i++) {
            const randomValue = Math.floor(Math.random() * (remainingSales / (30 - i)));
            remainingSales -= randomValue;
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + i);
            const options = { month: 'short', day: 'numeric' };
            const formattedDate = currentDate.toLocaleDateString('en-US', options);

            data.push({
                date: formattedDate,
                sales: randomValue,
            });
        }

        return data;
    };

    const generateRandomVisitData = () => {
        const startDate = new Date(dayjs().subtract(29, 'day').format('YYYY-MM-DD'));
        let remainingSales = 1551;
        const data = [];

        for (let i = 0; i < 30; i++) {
            const randomValue = Math.floor(Math.random() * (remainingSales / (30 - i)));
            remainingSales -= randomValue;
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + i);
            const options = { month: 'short', day: 'numeric' };
            const formattedDate = currentDate.toLocaleDateString('en-US', options);

            data.push({
                date: formattedDate,
                sales: randomValue,
            });
        }

        return data;
    };

    const [salesData, setSalesData] = useState([]);
    const [visitData, setVisitData] = useState([]);

    useEffect(() => {
        const data = generateRandomData();
        const visitData = generateRandomVisitData();
        setSalesData(data);
        setVisitData(visitData);
    }, []);

    const visitChartData = {
        labels: visitData.map(item => item.date),
        datasets: [
            {
                label: 'Visits',
                data: visitData.map(item => item.sales),
                borderColor: '#844f00',
                backgroundColor: 'transparent',
                borderWidth: 2,
                tension: 1,
                pointRadius: 3,
                pointHoverRadius: 8,
                pointBorderWidth: 1,
                pointBackgroundColor: 'transparent'
            },
        ],
    };

    const totalDuration = 500;
    const delayBetweenPoints = totalDuration / salesData.length;

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
            x: {
                grid: {
                    display: true,
                },
                display: true,
            },
            y: {
                display: true,
            },
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const label = context.dataset.label || '';
                        const value = context.raw;
                        return ` ${label}: ${value}`;
                    }
                }
            },
            legend: {
                display: false,
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle'
                }
            }
        },
        animation: {
            x: {
                type: 'number',
                easing: 'linear',
                duration: delayBetweenPoints,
                from: NaN,
                delay(ctx) {
                    if (ctx.type !== 'data' || ctx.xStarted) {
                        return 0;
                    }
                    ctx.xStarted = true;
                    return ctx.index * delayBetweenPoints;
                },
            },
            y: {
                type: 'number',
                easing: 'linear',
                duration: delayBetweenPoints,
                from: (ctx) => ctx.index === 0 ? ctx.chart.scales.y.getPixelForValue(100) : ctx.chart.getDatasetMeta(ctx.datasetIndex).data[ctx.index - 1].getProps(['y'], true).y,
                delay(ctx) {
                    if (ctx.type !== 'data' || ctx.yStarted) {
                        return 0;
                    }
                    ctx.yStarted = true;
                    return ctx.index * delayBetweenPoints;
                },
            },
        },
        elements: {
            line: {
                tension: 1,
                borderWidth: 2,
            },
            point: {
                radius: 0,
            },
        },
    };

    const barChartData = {
        labels: salesData.map(item => item.date),
        datasets: [
            {
                label: 'Monthly Sales',
                data: salesData.map(item => item.sales),
                backgroundColor: '#1F618D',
                borderWidth: 1,
            },
        ],
    };

    const columns = [
        {
            title: <Checkbox />,
            dataIndex: 'checkbox',
            render: () => <Checkbox />,
        },
        {
            title: 'Avatar',
            dataIndex: 'avatar',
            render: (_, record) => <Avatar><PiUserDuotone size={18} /></Avatar>,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            render: (_, record) => (
                <div>
                    <Title level={5} style={{ margin: 0 }}>{record?.user_name}</Title>
                    <Title level={5} style={{ margin: 0, fontSize: 14, fontWeight: 400, color: '#8f8f8f' }}>{record?.user_email}</Title>
                </div>
            ),
        },
        {
            title: 'Phone #.',
            dataIndex: 'user_phone',
            render: (_, record) => (`${record?.user_country_code}${record?.user_phone}`),
        },
        {
            title: 'Created at',
            dataIndex: 'created_at',
        },
        {
            title: 'Plan start date',
            dataIndex: 'start_date',
            render: (timestamp) => timestamp !== "" ? dayjs.unix(timestamp).format('YYYY-MM-DD') : '',
        },
        {
            title: 'Plan end date',
            dataIndex: 'end_date',
            render: (timestamp) => timestamp !== "" ? dayjs.unix(timestamp).format('YYYY-MM-DD') : '',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (status) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_, record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button icon={<EditOutlined />} onClick={() => handleEditPlan(record)} />
                    <Button icon={<EyeOutlined />} style={{ color: 'green', borderColor: 'green' }} />
                    <Popconfirm title="Delete user" description="Are you sure to delete this user?" okText="Yes" cancelText="No" onConfirm={() => handleDelete(record)}>
                        <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </div>
            ),
        },
    ];

    const handleEditPlan = async (record) => {
        setSelectedRecord(record);
        setModifyPlanDrawer(true);
    };

    const handleNewPlanSubmit = async () => {
        setPlanLoading(true);
        const token = localStorage.getItem("adminLoginToken");
        try {
            const values = await planform.validateFields();
            const startDateTimestamp = values.start_date ? dayjs(values.start_date).unix() : null;
            const endDateTimestamp = values.end_date ? dayjs(values.end_date).unix() : null;
            const response = await fetch(`${window.API_BASE_URL}/users/plan-modify`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ user_id: selectedRecord.user_id, payment_amount: values.payment_amount, transaction_id: values.transaction_id, start_date: startDateTimestamp, end_date: endDateTimestamp }),
            });

            if (response.ok) {
                const result = await response.json();
                message.success('Plan updated successfully.');
            } else {
                message.error("Error updating plan!");
            }
        } catch (error) {
            console.error("Error saving content:", error);
        } finally {
            setPlanLoading(false);
            setModifyPlanDrawer(false);
        }
    };

    const handleDelete = async () => {

    };

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchText(value);
        const filtered = users.filter((data) => {
            return (
                data.user_name.toLowerCase().includes(value) ||
                data.user_email.toLowerCase().includes(value) ||
                data.user_phone.toLowerCase().includes(value) ||
                data.status.toLowerCase().includes(value)
            );
        });
        setFilteredData(filtered);
    };

    const handleExport = () => {
        console.log('Export data to CSV or Excel');
    };

    const currentMonthActiveUsers = users?.filter(user =>
        user.status === 'active' && dayjs(user.created_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM') === currentMonth
    )?.length || 0;
    const previousMonthActiveUsers = users?.filter(user =>
        user.status === 'active' && dayjs(user.created_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM') === previousMonth
    )?.length || 0;
    const activechange = previousMonthActiveUsers
        ? ((currentMonthActiveUsers - previousMonthActiveUsers) / previousMonthActiveUsers) * 100
        : 0;
    const changeActiveSign = activechange >= 0 ? '+' : '';
    const changeActiveClass = activechange >= 0 ? 'positive' : 'negative';


    const currentMonthInActiveUsers = users?.filter(user =>
        user.status === 'inactive' && dayjs(user.created_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM') === currentMonth
    )?.length || 0;
    const previousMonthInActiveUsers = users?.filter(user =>
        user.status === 'inactive' && dayjs(user.created_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM') === previousMonth
    )?.length || 0;
    const changeinactive = previousMonthInActiveUsers
        ? ((currentMonthInActiveUsers - previousMonthInActiveUsers) / previousMonthInActiveUsers) * 100
        : 0;
    const changeInActiveSign = changeinactive >= 0 ? '+' : '';
    const changeInActiveClass = changeinactive >= 0 ? 'positive' : 'negative';

    const currentMonthInTrialUsers = users?.filter(user =>
        user.user_type === 'intrial' && dayjs(user.created_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM') === currentMonth
    )?.length || 0;
    const previousMonthInTrialUsers = users?.filter(user =>
        user.user_type === 'intrial' && dayjs(user.created_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM') === previousMonth
    )?.length || 0;
    const changeInTrial = previousMonthInTrialUsers
        ? ((currentMonthInTrialUsers - previousMonthInTrialUsers) / previousMonthInTrialUsers) * 100
        : 0;
    const changeInTrialSign = changeInTrial >= 0 ? '+' : '';
    const changeInTrialClass = changeInTrial >= 0 ? 'positive' : 'negative';

    const currentMonthPaidUsers = users?.filter(user =>
        user.user_type === 'paid' && dayjs(user.created_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM') === currentMonth
    )?.length || 0;
    const previousMonthPaidUsers = users?.filter(user =>
        user.user_type === 'paid' && dayjs(user.created_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM') === previousMonth
    )?.length || 0;
    const changePaid = previousMonthPaidUsers
        ? ((currentMonthPaidUsers - previousMonthPaidUsers) / previousMonthPaidUsers) * 100
        : 0;
    const changePaidSign = changePaid >= 0 ? '+' : '';
    const changePaidClass = changePaid >= 0 ? 'positive' : 'negative';

    return (
        <Layout className='contentLayout'>
            <Row gutter={[16, 16]}>
                <Col span={6}>
                    <Card bordered={false} hoverable className='dashboardOverviewCards lightgreen'>
                        <Flex gap={16} justify='space-between'>
                            <div>
                                <Title className='cardtitleOne' level={5}>Active Users</Title>
                                <Title className='cardtitleTwo' level={3}>{users?.filter(user => user.status === 'active')?.length} <span className={changeActiveClass}>({changeActiveSign}{activechange.toFixed(0)}%)</span></Title>
                                <Title className='cardtitleThree' level={5}>Total Users</Title>
                            </div>
                            <div>
                                <div className='cardIcon'>
                                    <PiUsersDuotone size={36} />
                                </div>
                            </div>
                        </Flex>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bordered={false} hoverable className='dashboardOverviewCards lightorange'>
                        <Flex gap={16} justify='space-between'>
                            <div>
                                <Title className='cardtitleOne' level={5}>Inactive Users</Title>
                                <Title className='cardtitleTwo' level={3}>{users?.filter(user => user.status === 'inactive')?.length} <span className={changeInActiveClass}>({changeInActiveSign}{changeinactive.toFixed(0)}%)</span></Title>
                                <Title className='cardtitleThree' level={5}>Last Week Analytics</Title>
                            </div>
                            <div>
                                <div className='cardIcon'>
                                    <PiUserFocusDuotone size={36} />
                                </div>
                            </div>
                        </Flex>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bordered={false} hoverable className='dashboardOverviewCards lightpink'>
                        <Flex gap={16} justify='space-between'>
                            <div>
                                <Title className='cardtitleOne' level={5}>Trial Customers</Title>
                                <Title className='cardtitleTwo' level={3}>{users?.filter(user => user.user_type === 'intrial')?.length} <span className={changeInTrialClass}>({changeInTrialSign}{changeInTrial.toFixed(0)}%)</span></Title>
                                <Title className='cardtitleThree' level={5}>Last Week Analytics</Title>
                            </div>
                            <div>
                                <div className='cardIcon'>
                                    <PiUserPlusDuotone size={36} />
                                </div>
                            </div>
                        </Flex>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bordered={false} hoverable className='dashboardOverviewCards lightyellow'>
                        <Flex gap={16} justify='space-between'>
                            <div>
                                <Title className='cardtitleOne' level={5}>Paid Customers</Title>
                                <Title className='cardtitleTwo' level={3}>{users?.filter(user => user.user_type === 'paid')?.length} <span className={changePaidClass}>({changePaidSign}{changePaid.toFixed(0)}%)</span></Title>
                                <Title className='cardtitleThree' level={5}>Last Week Analytics</Title>
                            </div>
                            <div>
                                <div className='cardIcon'>
                                    <PiUserSoundDuotone size={36} />
                                </div>
                            </div>
                        </Flex>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bordered={false} hoverable className='dashboardOverviewCards lightpurple'>
                        <Flex gap={16} justify='space-between'>
                            <div>
                                <Title className='cardtitleOne' level={5}>Payment Received</Title>
                                <Title className='cardtitleTwo' level={3}>21,459 <span className='positive'>(+29%)</span></Title>
                                <Title className='cardtitleThree' level={5}>Total Users</Title>
                            </div>
                            <div>
                                <div className='cardIcon'>
                                    <PiCoinsDuotone size={36} />
                                </div>
                            </div>
                        </Flex>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bordered={false} hoverable className='dashboardOverviewCards lightblue'>
                        <Flex gap={16} justify='space-between'>
                            <div>
                                <Title className='cardtitleOne' level={5}>Due Payment</Title>
                                <Title className='cardtitleTwo' level={3}>4,567 <span className='positive'>(+18%)</span></Title>
                                <Title className='cardtitleThree' level={5}>Last Week Analytics</Title>
                            </div>
                            <div>
                                <div className='cardIcon'>
                                    <PiCoinsDuotone size={36} />
                                </div>
                            </div>
                        </Flex>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bordered={false} hoverable className='dashboardOverviewCards greenblue'>
                        <Flex gap={16} justify='space-between'>
                            <div>
                                <Title className='cardtitleOne' level={5}>Total Discount Given</Title>
                                <Title className='cardtitleTwo' level={3}>19,867 <span className='negative'>(-18%)</span></Title>
                                <Title className='cardtitleThree' level={5}>Last Week Analytics</Title>
                            </div>
                            <div>
                                <div className='cardIcon'>
                                    <PiGiftDuotone size={36} />
                                </div>
                            </div>
                        </Flex>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bordered={false} hoverable className='dashboardOverviewCards lightgreen'>
                        <Flex gap={16} justify='space-between'>
                            <div>
                                <Title className='cardtitleOne' level={5}>Yearly Plan Sold</Title>
                                <Title className='cardtitleTwo' level={3}>237 <span className='positive'>(+42%)</span></Title>
                                <Title className='cardtitleThree' level={5}>Last Week Analytics</Title>
                            </div>
                            <div>
                                <div className='cardIcon'>
                                    <PiListPlusDuotone size={36} />
                                </div>
                            </div>
                        </Flex>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card bordered={false} hoverable className='dashboardOverviewCards lightgreen'>
                        <Flex justify='space-between' align='center' style={{ marginBottom: 24 }}>
                            <Title level={4} style={{ margin: 0 }}>Sales Source</Title>
                            <FiMoreVertical />
                        </Flex>
                        <Flex justify='center'>
                            <div className='doughnutDiv'>
                                <Doughnut data={donughtData} options={donughtOptions} />
                            </div>
                        </Flex>
                        <Flex justify='space-between' align='center' style={{ marginTop: 24 }}>
                            <div style={{ textAlign: 'center' }}>
                                <Title level={4} style={{ margin: 0 }}>5,459</Title>
                                <Title level={5} style={{ margin: 0, fontSize: 14, fontWeight: 400, color: 'grey' }}>Total Users</Title>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <Title level={4} style={{ margin: 0 }}>18</Title>
                                <Title level={5} style={{ margin: 0, fontSize: 14, fontWeight: 400, color: 'grey' }}>Open Campaigns</Title>
                            </div>
                        </Flex>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card bordered={false} hoverable className='dashboardOverviewCards lightorange'>
                        <Flex justify='space-between' align='center' style={{ marginBottom: 24 }}>
                            <Title level={4} style={{ margin: 0 }}>Monthly Sales Source</Title>
                            <FiMoreVertical />
                        </Flex>
                        <Flex justify='center'>
                            <div className='chartdiv'>
                                <Bar data={barChartData} options={options} />
                            </div>
                        </Flex>
                        <Flex justify='space-between' align='center' style={{ marginTop: 24 }}>
                            <div style={{ textAlign: 'center' }}>
                                <Title level={4} style={{ margin: 0 }}>$1875.54</Title>
                                <Title level={5} style={{ margin: 0, fontSize: 14, fontWeight: 400, color: 'grey' }}>Revenue</Title>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <Title level={4} style={{ margin: 0 }}>541</Title>
                                <Title level={5} style={{ margin: 0, fontSize: 14, fontWeight: 400, color: 'grey' }}>Total Offers</Title>
                            </div>
                        </Flex>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card bordered={false} hoverable className='dashboardOverviewCards lightpink'>
                        <Flex justify='space-between' align='center' style={{ marginBottom: 24 }}>
                            <Title level={4} style={{ margin: 0 }}>Revenue Source</Title>
                            <FiMoreVertical />
                        </Flex>
                        <Flex justify='center'>
                            <div className='chartdiv'>
                                <Line data={visitChartData} options={options} />
                            </div>
                        </Flex>
                        <Flex justify='space-between' align='center' style={{ marginTop: 24 }}>
                            <div style={{ textAlign: 'center' }}>
                                <Title level={4} style={{ margin: 0 }}>$7,841.12</Title>
                                <Title level={5} style={{ margin: 0, fontSize: 14, fontWeight: 400, color: 'grey' }}>Total Revenue</Title>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <Title level={4} style={{ margin: 0 }}>18</Title>
                                <Title level={5} style={{ margin: 0, fontSize: 14, fontWeight: 400, color: 'grey' }}>Open Campaigns</Title>
                            </div>
                        </Flex>
                    </Card>
                </Col>
                <Col span={24}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                        <Input
                            placeholder="Search..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={handleSearch}
                            style={{ width: 200 }}
                        />
                        <Button
                            icon={<ExportOutlined />}
                            onClick={handleExport}
                        >
                            Export
                        </Button>
                    </div>
                    <Table
                        dataSource={searchText ? filteredData : users}
                        columns={columns}
                        pagination={{ pageSize: 10 }}
                        loading={dataLoading}
                    />
                </Col>
            </Row>
            <Drawer
                title={<><UserAddOutlined /> Modify Plan</>}
                width={720}
                onClose={() => setModifyPlanDrawer(false)}
                open={modifyPlanDrawer}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setModifyPlanDrawer(false)}>Cancel</Button>
                            {loading ? (
                                <Button type="primary">
                                    <Spin style={{ color: "#fff" }} indicator={<LoadingOutlined spin />} />
                                </Button>
                            ) : (
                                <Button onClick={handleNewPlanSubmit} type="primary" loading={planLoading}>
                                    <span>Submit</span>
                                </Button>
                            )}
                        </Space>
                    </div>
                }
            >
                <Form layout="vertical" form={planform}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="payment_amount"
                                label="Payment Amount"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter payment amount',
                                    },
                                ]}
                            >
                                <Input placeholder="Please enter payment amount" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="transaction_id"
                                label="Transaction Id"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter transaction id',
                                    },
                                ]}
                            >
                                <Input placeholder="Please enter transaction id" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="start_date"
                                label="Plan start date"
                                rules={[
                                    {
                                        required: false,
                                        message: 'Please enter plan start date',
                                    },
                                ]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="end_date"
                                label="Plan end date"
                                rules={[
                                    {
                                        required: false,
                                        message: 'Please enter Plan end date',
                                    },
                                ]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Drawer>
        </Layout>
    );
};
export default Home;